import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Skeleton, Alert, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, IconButton, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useDeviceData } from '../../context/DeviceDataContext';
import { getDevices, createDevice, deleteDevice, updateDevice, getCompanies } from '../../utils/clientUtils';
import CompanyTreePicker from '../../components/CompanyTreePicker';

export default function Devices() {
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);

  const { devicesData, loading: deviceTypesLoading } = useDeviceData();

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [companies, setCompanies] = useState([]);

  // Add form state
  const [newDeviceID, setNewDeviceID] = useState('');
  const [newDeviceTypeId, setNewDeviceTypeId] = useState('');
  const [newPath, setNewPath] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edit dialog state
  const [editTarget, setEditTarget] = useState(null); // { deviceID, path }
  const [editPath, setEditPath] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchAuthSession().then(session => {
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'] ?? [];
      setIsAdmin(groups.includes('device_management'));
      setAdminChecked(true);
    });
  }, []);

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    const data = await getDevices();
    setDevices(Array.isArray(data) ? data : []);
    setDevicesLoading(false);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadDevices();
    getCompanies().then(data => setCompanies(Array.isArray(data) ? data : []));
  }, [isAdmin, loadDevices]);

  const deviceTypesList = devicesData?.devices?.types
    ? [...devicesData.devices.types].sort((a, b) => a.dtid - b.dtid)
    : [];

  const typeNameById = {};
  deviceTypesList.forEach(dt => { typeNameById[dt.dtid] = dt.devicename; });

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    if (!newDeviceID.trim() || !newDeviceTypeId || !newPath) {
      setFormError(t('devices.allFieldsRequired'));
      return;
    }
    setSubmitting(true);
    const result = await createDevice({
      deviceID: newDeviceID.trim(),
      deviceTypeId: Number(newDeviceTypeId),
      path: newPath
    });
    setSubmitting(false);
    if (result && result.deviceID) {
      setFormSuccess(t('devices.deviceAddedSuccess', { id: result.deviceID }));
      setNewDeviceID('');
      setNewDeviceTypeId('');
      setNewPath('');
      loadDevices();
    } else {
      setFormError(result?.error || t('devices.failedToAddDevice'));
    }
  };

  const handleEditOpen = (device) => {
    setEditTarget(device);
    const normalize = p => p?.replace(/\/$/, '') ?? '';
    const match = companies.find(c => normalize(c.path) === normalize(device.path));
    setEditPath(match?.path ?? device.path);
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditError('');
    setEditSaving(true);
    const result = await updateDevice(editTarget.deviceID, { path: editPath });
    setEditSaving(false);
    if (result && result.deviceID) {
      setEditTarget(null);
      loadDevices();
    } else {
      setEditError(result?.error || t('devices.failedToUpdateDevice'));
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteError('');
    const result = await deleteDevice(deleteTarget);
    if (result && result.deviceID) {
      setDeleteTarget(null);
      loadDevices();
    } else {
      setDeleteError(result?.error || t('devices.failedToDeleteDevice'));
    }
  };

  if (!adminChecked) return null;

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h2" sx={{ color: 'primary.main', textAlign: 'left' }}>{t('devices.title')}</Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>{t('devices.accessDenied')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h2" sx={{ color: 'primary.main', textAlign: 'left' }}>{t('devices.title')}</Typography>

      {/* Devices list */}
      <Typography variant="h5" sx={{ mb: 1 }}>{t('devices.registeredDevices')}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('devices.deviceId')}</TableCell>
              <TableCell>{t('devices.type')}</TableCell>
              <TableCell>{t('devices.companyPath')}</TableCell>
              <TableCell align="right">{t('devices.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devicesLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              : devices.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                      {t('devices.noDevicesRegistered')}
                    </TableCell>
                  </TableRow>
                )
                : devices.map(d => (
                  <TableRow key={d.deviceID}>
                    <TableCell>{d.deviceID}</TableCell>
                    <TableCell>{typeNameById[d.deviceTypeId] ?? d.deviceTypeId}</TableCell>
                    <TableCell>{d.path}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditOpen(d)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setDeleteError(''); setDeleteTarget(d.deviceID); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 3 }} />

      {/* Add Device form */}
      <Typography variant="h5" sx={{ mb: 2 }}>{t('devices.addDevice')}</Typography>
      <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
        <TextField
          label={t('devices.deviceId')}
          value={newDeviceID}
          onChange={e => setNewDeviceID(e.target.value)}
          required
          size="small"
        />
        <FormControl size="small" required>
          <InputLabel>{t('devices.deviceType')}</InputLabel>
          <Select
            value={newDeviceTypeId}
            label={t('devices.deviceType')}
            onChange={e => setNewDeviceTypeId(e.target.value)}
            disabled={deviceTypesLoading}
          >
            {deviceTypesList.map(dt => (
              <MenuItem key={dt.dtid} value={dt.dtid}>{dt.devicename}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{t('devices.company')} *</Typography>
          <CompanyTreePicker companies={companies} value={newPath} onChange={setNewPath} />
        </Box>
        {formError && <Alert severity="error">{formError}</Alert>}
        {formSuccess && <Alert severity="success">{formSuccess}</Alert>}
        <Box>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? t('devices.adding') : t('devices.addDevice')}
          </Button>
        </Box>
      </Box>

      {/* Edit company path dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('devices.editCompanyPath')}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText sx={{ mb: 2 }}>
            {t('devices.device')}: {editTarget?.deviceID}
          </DialogContentText>
          <CompanyTreePicker companies={companies} value={editPath} onChange={setEditPath} />
          {editError && <Alert severity="error" sx={{ mt: 1 }}>{editError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving || !editPath}>
            {editSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('devices.deleteDevice')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('devices.confirmDelete', { id: deleteTarget })}
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDeleteConfirm}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
